import { useState } from 'react';
import { Panel } from '../../components/Panel';
import { Pager } from '../../components/Pager';
import { Button } from '../../components/Button';
import { Spinner, ErrorBox } from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { listCustomers, listProducts, listTodoke, listStaff, listWarehouses, listStock, listSites, masterSyncPreview, masterSyncApply, masterSyncHistory, masterSyncTargets } from '../../api/endpoints';
import type { MasterSyncResult, MasterSyncSample, MasterSyncHistoryRecord } from '../../types/api';

const PER_PAGE = 50;
type Tab = 'customer' | 'product' | 'todoke' | 'staff' | 'site' | 'warehouse' | 'stock' | 'sync';

export function MasterPage() {
  const [tab, setTab] = useState<Tab>('customer');

  const tabs: { key: Tab; label: string }[] = [
    { key: 'customer', label: '顧客マスタ' },
    { key: 'product', label: '商品マスタ' },
    { key: 'todoke', label: '届け先マスタ' },
    { key: 'staff', label: '担当者マスタ' },
    { key: 'site', label: '現場マスタ' },
    { key: 'warehouse', label: '倉庫マスタ' },
    { key: 'stock', label: '本社在庫マスタ' },
    { key: 'sync', label: 'ERP同期' },
  ];

  return (
    <Panel title="マスタ管理">
      <div className="flex gap-1 mb-4 border-b">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'customer' && <CustomerList />}
      {tab === 'product' && <ProductList />}
      {tab === 'todoke' && <TodokeList />}
      {tab === 'staff' && <StaffList />}
      {tab === 'site' && <SiteList />}
      {tab === 'warehouse' && <WarehouseList />}
      {tab === 'stock' && <StockList />}
      {tab === 'sync' && <SyncPanel />}
    </Panel>
  );
}

// ── ERP マスタ同期（dry-run 差分確認 / 本番反映）──
function SyncPanel() {
  const [result, setResult] = useState<MasterSyncResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [running, setRunning] = useState<null | 'preview' | 'apply'>(null);
  const [historyKey, setHistoryKey] = useState(0);

  // 同期対象マスタの候補。未選択(=全チェック)のときは全マスタ同期になる。
  const { data: targetData, loading: targetLoading, error: targetError } = useApi(() => masterSyncTargets(), []);
  const allTargets = targetData?.targets ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // 候補が読み込めたら初期状態は全選択にする（従来どおり全マスタ対象）。
  const selectedKeys = selected.size ? selected : new Set(allTargets.map((t) => t.name));
  const chosen = allTargets.filter((t) => selectedKeys.has(t.name)).map((t) => t.name);
  const allChecked = chosen.length === allTargets.length;

  function toggle(name: string) {
    // 初回操作時は「現在の見た目（全選択）」を実体化してから切り替える。
    const base = new Set(selectedKeys);
    if (base.has(name)) base.delete(name);
    else base.add(name);
    setSelected(base);
  }

  function toggleAll() {
    setSelected(allChecked ? new Set(['__none__']) : new Set(allTargets.map((t) => t.name)));
  }

  async function run(mode: 'preview' | 'apply') {
    if (chosen.length === 0) {
      setError(new Error('同期するマスタを1つ以上選択してください'));
      return;
    }
    const scope = allChecked ? '全マスタ' : allTargets.filter((t) => selectedKeys.has(t.name)).map((t) => t.label).join('・');
    if (mode === 'apply' && !window.confirm(
      `ERPから取得したマスタを本番反映します（Blob/Cosmosを更新）。\n` +
      `対象: ${scope}\n` +
      '先にdry-runで差分を確認済みですか？ 実行してよろしいですか？',
    )) return;
    setRunning(mode);
    setError(null);
    setResult(null);
    try {
      // 全選択時は targets を送らない（＝サーバ側で全マスタ扱い、従来と同じ）
      const t = allChecked ? undefined : chosen;
      const data = mode === 'preview' ? await masterSyncPreview(t) : await masterSyncApply(t);
      setResult(data);
    } catch (e) {
      setError(e as Error);
    } finally {
      setRunning(null);
      if (mode === 'apply') setHistoryKey((k) => k + 1);  // 反映後は履歴を更新
    }
  }

  return (
    <div>
      <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-4 text-sm text-slate-600">
        ERP(カクテル)へVPN経由で接続し、顧客・現場・届先・担当者・在庫マスタ(CSV)と商品マスタ(Cosmos)を同期します。
        下の<span className="font-medium">対象マスタ</span>で同期するマスタを選び（既定は全選択）、
        まず<span className="font-medium">「差分を確認 (dry-run)」</span>で追加/変更/削除の件数を確認し、
        問題なければ<span className="font-medium">「本番反映」</span>で実際に更新してください。
        （毎日03:00に全マスタの自動同期も実行されます）
      </div>

      <div className="border border-slate-200 rounded p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">対象マスタ</span>
          <button className="text-xs text-primary" disabled={running !== null} onClick={toggleAll}>
            {allChecked ? 'すべて解除' : 'すべて選択'}
          </button>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {allTargets.length === 0 && targetLoading && (
            <span className="text-xs text-slate-400">読み込み中...</span>
          )}
          {allTargets.length === 0 && !targetLoading && (
            <span className="text-xs text-danger">
              対象マスタを取得できませんでした{targetError ? `（${targetError.message}）` : ''}
            </span>
          )}
          {allTargets.map((t) => (
            <label key={t.name} className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedKeys.has(t.name)}
                disabled={running !== null}
                onChange={() => toggle(t.name)}
              />
              {t.label}
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant="primary" disabled={running !== null} onClick={() => run('preview')}>
          {running === 'preview' ? '確認中...' : '差分を確認 (dry-run)'}
        </Button>
        <Button variant="danger" disabled={running !== null} onClick={() => run('apply')}>
          {running === 'apply' ? '反映中...' : '本番反映を実行'}
        </Button>
      </div>

      {running && <Spinner text={running === 'preview' ? 'ERPに接続して差分を算出中...' : 'ERPから取得してBlob/Cosmosに反映中...'} />}
      {error && <ErrorBox error={error} />}
      {result && !running && <SyncResultView result={result} />}

      <div className="mt-8 border-t pt-4">
        <SyncHistory reloadKey={historyKey} />
      </div>
    </div>
  );
}

// 同期実行履歴（Timer=自動 / 手動apply の両方、何を何件更新したか）
function SyncHistory({ reloadKey }: { reloadKey: number }) {
  const { data, loading, error } = useApi(() => masterSyncHistory(), [reloadKey]);

  const statusBadge = (s: MasterSyncHistoryRecord['status']) => {
    const map = {
      success: ['成功', 'bg-green-50 text-green-700 border-green-200'],
      failed: ['失敗', 'bg-red-50 text-danger border-red-200'],
      running: ['実行中', 'bg-amber-50 text-amber-600 border-amber-200'],
      interrupted: ['中断', 'bg-red-50 text-danger border-red-200'],
    } as const;
    const [label, cls] = map[s] ?? [s, 'bg-slate-50 text-slate-500 border-slate-200'];
    return <span className={`px-2 py-0.5 rounded text-xs border ${cls}`}>{label}</span>;
  };

  return (
    <div>
      <h3 className="text-sm font-medium text-slate-700 mb-2">同期履歴</h3>
      {loading && <Spinner text="履歴を読み込み中..." />}
      {error && <ErrorBox error={error} />}
      {!loading && !error && (data?.items?.length ?? 0) === 0 && (
        <p className="text-sm text-slate-400">まだ同期履歴がありません。</p>
      )}
      {!loading && !error && (data?.items?.length ?? 0) > 0 && (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-4">実行日時</th>
              <th className="py-2 pr-4">種別</th>
              <th className="py-2 pr-4">実行者</th>
              <th className="py-2 pr-4">結果</th>
              <th className="py-2 pr-4 text-right">追加</th>
              <th className="py-2 pr-4 text-right">変更</th>
              <th className="py-2 pr-4 text-right">削除</th>
              <th className="py-2 pr-4 text-right">所要</th>
              <th className="py-2 pr-4">内訳</th>
            </tr>
          </thead>
          <tbody>
            {data!.items.map((h) => (
              <tr key={h.id} className="border-b align-top">
                <td className="py-2 pr-4 font-mono text-xs">{h.run_at.replace('T', ' ').slice(0, 19)}</td>
                <td className="py-2 pr-4">{h.trigger === 'timer' ? '自動(03:00)' : '手動'}</td>
                <td className="py-2 pr-4 text-xs">{h.user || '—'}</td>
                <td className="py-2 pr-4">{statusBadge(h.status)}</td>
                <td className="py-2 pr-4 text-right font-mono text-green-700">{h.totals.added}</td>
                <td className="py-2 pr-4 text-right font-mono text-amber-600">{h.totals.changed}</td>
                <td className="py-2 pr-4 text-right font-mono text-danger">{h.totals.deleted}</td>
                <td className="py-2 pr-4 text-right font-mono text-xs">{h.duration_sec}s</td>
                <td className="py-2 pr-4">
                  <details>
                    <summary className="cursor-pointer text-xs text-primary">詳細</summary>
                    <div className="mt-1 space-y-0.5">
                      {h.masters.map((m) => (
                        <div key={m.name} className="text-xs text-slate-600 font-mono">
                          {m.label}: 追加{m.added} 変更{m.changed} 削除{m.deleted}
                          {m.upserted != null && ` (upsert${m.upserted}${m.errors ? `/エラー${m.errors}` : ''})`}
                        </div>
                      ))}
                      {h.error && <div className="text-xs text-danger">エラー: {h.error}</div>}
                    </div>
                  </details>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// サンプルを 追加/変更/削除 ごとに色分け・名称付きで表示
function SampleCell({ added, changed, deleted }: {
  added?: MasterSyncSample[]; changed?: MasterSyncSample[]; deleted?: MasterSyncSample[];
}) {
  const groups = [
    { label: '追加', items: added ?? [], cls: 'text-green-700' },
    { label: '変更', items: changed ?? [], cls: 'text-amber-600' },
    { label: '削除', items: deleted ?? [], cls: 'text-danger' },
  ].filter((g) => g.items.length > 0);

  if (groups.length === 0) return <span className="text-slate-400">差分なし</span>;

  return (
    <div className="space-y-1">
      {groups.map((g) => (
        <div key={g.label} className="flex gap-2 items-baseline">
          <span className={`shrink-0 text-xs font-medium ${g.cls}`}>{g.label}</span>
          <span className="text-xs text-slate-600">
            {g.items.slice(0, 3).map((s, i) => (
              <span key={s.key}>
                {i > 0 && <span className="text-slate-300">／</span>}
                <span className="font-mono">{s.key}</span>
                {s.name && <span className="text-slate-500">（{s.name}）</span>}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

function SyncResultView({ result }: { result: MasterSyncResult }) {
  const p = result.product;
  return (
    <div className="space-y-4">
      <div className={`inline-block px-3 py-1 rounded text-sm font-medium ${
        result.dry_run ? 'bg-blue-50 text-primary border border-blue-200' : 'bg-green-50 text-green-700 border border-green-200'
      }`}>
        {result.dry_run ? 'DRY-RUN（差分のみ・DB未更新）' : '本番反映 完了（DB更新済み）'}
      </div>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <th className="py-2 pr-4">マスタ</th>
            <th className="py-2 pr-4 text-right">総件数</th>
            <th className="py-2 pr-4 text-right">追加</th>
            <th className="py-2 pr-4 text-right">変更</th>
            <th className="py-2 pr-4 text-right">削除</th>
            <th className="py-2 pr-4">サンプル（コード（名称）を最大3件ずつ表示）</th>
          </tr>
        </thead>
        <tbody>
          {result.csv_masters.map((m) => (
            <tr key={m.name} className="border-b align-top">
              <td className="py-2 pr-4 font-medium">{m.label}</td>
              <td className="py-2 pr-4 text-right font-mono">{m.rows}</td>
              <td className="py-2 pr-4 text-right font-mono text-green-700">{m.added}</td>
              <td className="py-2 pr-4 text-right font-mono text-amber-600">{m.changed}</td>
              <td className="py-2 pr-4 text-right font-mono text-danger">{m.deleted}</td>
              <td className="py-2 pr-4">
                <SampleCell added={m.sample_added} changed={m.sample_changed} deleted={m.sample_deleted} />
              </td>
            </tr>
          ))}
          {p && (
            <tr className="border-b align-top bg-slate-50">
              <td className="py-2 pr-4 font-medium">
                {p.label}
                <span className="ml-2 text-xs text-slate-400">ERP {p.erp}件{p.cosmos != null ? ` / Cosmos ${p.cosmos}件` : ''}</span>
              </td>
              <td className="py-2 pr-4 text-right font-mono">{p.erp}</td>
              <td className="py-2 pr-4 text-right font-mono text-green-700">{p.added}</td>
              <td className="py-2 pr-4 text-right font-mono text-amber-600">{p.changed}</td>
              <td className="py-2 pr-4 text-right font-mono text-danger">{p.deleted}</td>
              <td className="py-2 pr-4">
                {result.dry_run
                  ? <SampleCell added={p.sample_added} changed={p.sample_changed} deleted={p.sample_deleted} />
                  : <span className="text-xs text-slate-500 font-mono">upsert {p.upserted ?? 0}件{p.errors ? ` / エラー${p.errors}件` : ''}</span>}
                {/* 名称ﾏﾆｭｱﾙ入力区分だけの差分は embedding を作り直さずフィールドのみ更新する。
                    追加/変更/削除が0でもここに件数が出るため「差分なし」と誤解されない。 */}
                {!!p.flag_updated && (
                  <div className="text-xs text-slate-500 font-mono mt-1">
                    名称ﾏﾆｭｱﾙ入力区分 {result.dry_run ? '要更新' : '更新'} {p.flag_updated}件
                  </div>
                )}
              </td>
            </tr>
          )}
          {result.csv_masters.length === 0 && !p && (
            <tr><td colSpan={6} className="py-3 text-center text-sm text-slate-400">対象マスタがありません</td></tr>
          )}
        </tbody>
      </table>

      <p className="text-xs text-slate-400">
        ※ コードは各マスタの主キー。複合キーは「＋」で連結（現場＝得意先＋納品先、在庫＝倉庫＋商品）。
        名称は追加/変更は新データ、削除は前回データから表示しています。
      </p>
    </div>
  );
}

function CustomerList() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const { data, loading, error } = useApi(
    () => listCustomers({ q: appliedQ, page, per_page: PER_PAGE }),
    [appliedQ, page],
  );

  function handleSearch() {
    setPage(1);
    setAppliedQ(q);
  }

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="コード・名称・電話番号で検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">
            {data.total} 件中 {data.items.length ? (data.page - 1) * data.per_page + 1 : 0}〜
            {Math.min(data.page * data.per_page, data.total)} 件
          </p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>得意先コード</th>
                    <th>名称1</th>
                    <th>名称2</th>
                    <th>略称</th>
                    <th>電話番号</th>
                    <th>FAX</th>
                    <th>債権管理部門コード</th>
                    <th>担当者コード</th>
                    <th>名称変更</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.code}>
                      <td className="text-xs font-mono">{r.code}</td>
                      <td>{r.name1}</td>
                      <td>{r.name2}</td>
                      <td>{r.abbr}</td>
                      <td>{r.phone}</td>
                      <td>{r.fax}</td>
                      <td className="text-xs font-mono">{r.department_code}</td>
                      <td className="text-xs font-mono">{r.staff_code}</td>
                      <td className="text-center">{r.name_editable ? <span className="text-xs text-primary">可</span> : <span className="text-xs text-muted">不可</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} perPage={PER_PAGE} total={data.total} onChange={setPage} />
        </>
      )}
    </>
  );
}

function ProductList() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const { data, loading, error } = useApi(
    () => listProducts({ q: appliedQ, page, per_page: PER_PAGE }),
    [appliedQ, page],
  );

  function handleSearch() {
    setPage(1);
    setAppliedQ(q);
  }

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="商品コード・商品名で検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">
            {data.total} 件中 {data.items.length ? (data.page - 1) * data.per_page + 1 : 0}〜
            {Math.min(data.page * data.per_page, data.total)} 件
          </p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>商品コード</th>
                    <th>商品名1</th>
                    <th>商品名2</th>
                    <th>検索テキスト</th>
                    <th>分類コード1</th>
                    <th>分類コード2</th>
                    <th>分類コード3</th>
                    <th>分類名称1</th>
                    <th>分類名称2</th>
                    <th>分類名称3</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={`${r['商品コード']}-${i}`}>
                      <td className="text-xs font-mono">{r['商品コード'] || ''}</td>
                      <td>{r['商品名1'] || ''}</td>
                      <td>{r['商品名2'] || ''}</td>
                      <td className="text-xs text-muted">{r['検索テキスト'] || ''}</td>
                      <td className="text-xs font-mono">{r['分類コード1'] || ''}</td>
                      <td className="text-xs font-mono">{r['分類コード2'] || ''}</td>
                      <td className="text-xs font-mono">{r['分類コード3'] || ''}</td>
                      <td className="text-xs text-muted">{r['分類名称1'] || ''}</td>
                      <td className="text-xs text-muted">{r['分類名称2'] || ''}</td>
                      <td className="text-xs text-muted">{r['分類名称3'] || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} perPage={PER_PAGE} total={data.total} onChange={setPage} />
        </>
      )}
    </>
  );
}

function TodokeList() {
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const { data, loading, error } = useApi(
    () => listTodoke({ q: appliedQ }),
    [appliedQ],
  );

  function handleSearch() {
    setAppliedQ(q);
  }

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="コード・名称で検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">{data.total} 件</p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>届け先コード</th>
                    <th>届け先名称</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.code}>
                      <td className="text-xs font-mono">{r.code}</td>
                      <td>{r.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function StaffList() {
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const { data, loading, error } = useApi(
    () => listStaff({ q: appliedQ }),
    [appliedQ],
  );

  function handleSearch() {
    setAppliedQ(q);
  }

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="コード・名前・カナで検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">{data.total} 件</p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>担当者コード</th>
                    <th>担当者名</th>
                    <th>カナ</th>
                    <th>売上仕入部門コード</th>
                    <th>部門名</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.code}>
                      <td className="text-xs font-mono">{r.code}</td>
                      <td>{r.name}</td>
                      <td className="text-xs text-muted">{r.kana}</td>
                      <td className="text-xs font-mono text-muted">{r.department_code}</td>
                      <td className="text-xs text-muted">{r.department}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function SiteList() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [tokcd, setTokcd] = useState('');
  const [appliedTokcd, setAppliedTokcd] = useState('');

  const { data, loading, error } = useApi(
    () => listSites({ tokcd: appliedTokcd, q: appliedQ, page, per_page: PER_PAGE }),
    [appliedTokcd, appliedQ, page],
  );

  function handleSearch() {
    setPage(1);
    setAppliedQ(q);
    setAppliedTokcd(tokcd);
  }

  return (
    <>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="得意先コード"
          value={tokcd}
          onChange={(e) => setTokcd(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="border border-border rounded px-3 py-2 text-sm w-40 font-mono"
        />
        <input
          type="text"
          placeholder="現場名・コードで検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">
            {data.total} 件中 {data.items.length ? (data.page - 1) * data.per_page + 1 : 0}〜
            {Math.min(data.page * data.per_page, data.total)} 件
          </p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>得意先コード</th>
                    <th>納品先コード</th>
                    <th>名称</th>
                    <th>略称</th>
                    <th>名称変更</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={`${r.code}-${i}`}>
                      <td className="text-xs font-mono">{r.customer_code}</td>
                      <td className="text-xs font-mono">{r.code}</td>
                      <td>{r.name}</td>
                      <td className="text-xs text-muted">{r.abbr}</td>
                      <td className="text-center">{r.name_editable ? <span className="text-xs text-primary">可</span> : <span className="text-xs text-muted">不可</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} perPage={PER_PAGE} total={data.total} onChange={setPage} />
        </>
      )}
    </>
  );
}

function WarehouseList() {
  const { data, loading, error } = useApi(() => listWarehouses(), []);

  return (
    <>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">{data.total} 件</p>
          <div className="overflow-auto">
            <table>
              <thead>
                <tr>
                  <th>倉庫コード</th>
                  <th>倉庫名</th>
                  <th>在庫管理部門コード</th>
                  <th>部門名</th>
                  <th>拠点コード</th>
                  <th>拠点略称</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r, i) => (
                  <tr key={i}>
                    <td className="text-xs font-mono">{r['倉庫コード']}</td>
                    <td>{r['倉庫名']}</td>
                    <td className="text-xs font-mono">{r['在庫管理部門コード']}</td>
                    <td className="text-xs text-muted">{r['部門名']}</td>
                    <td className="text-xs font-mono">{r['拠点コード']}</td>
                    <td className="text-xs text-muted">{r['拠点略称']}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function StockList() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [zone, setZone] = useState('');

  const { data, loading, error } = useApi(
    () => listStock({ q: appliedQ, page, per_page: PER_PAGE, zone }),
    [appliedQ, page, zone],
  );

  function handleSearch() {
    setPage(1);
    setAppliedQ(q);
  }

  return (
    <>
      <div className="flex gap-2 mb-3 items-end">
        <input
          type="text"
          placeholder="商品コード・品名・規格で検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <select
          className="border border-border rounded px-3 py-2 text-sm"
          value={zone}
          onChange={(e) => { setZone(e.target.value); setPage(1); }}
        >
          <option value="">全ゾーン</option>
          <option value="010">Aゾーン (010)</option>
          <option value="011">Bゾーン (011)</option>
          <option value="012">Cゾーン (012)</option>
          <option value="000">0ゾーン (000)</option>
        </select>
        <Button onClick={handleSearch}>検索</Button>
      </div>
      {loading && <Spinner />}
      {error && <ErrorBox error={error} />}
      {data && (
        <>
          <p className="text-sm text-muted mb-2">
            {data.total} 件中 {data.items.length ? (data.page - 1) * data.per_page + 1 : 0}〜
            {Math.min(data.page * data.per_page, data.total)} 件
          </p>
          {data.items.length === 0 ? (
            <p className="text-muted">該当なし</p>
          ) : (
            <div className="overflow-auto">
              <table>
                <thead>
                  <tr>
                    <th>倉庫コード</th>
                    <th>倉庫名</th>
                    <th>商品大分類</th>
                    <th>商品中分類</th>
                    <th>商品小分類</th>
                    <th>商品コード</th>
                    <th>品名</th>
                    <th>規格</th>
                    <th>ゾーンコード</th>
                    <th>ゾーン名</th>
                    <th>エリア</th>
                    <th>アドレス</th>
                    <th>サブアドレス</th>
                    <th>発注点数</th>
                    <th>最大在庫数</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={i}>
                      <td className="text-xs font-mono">{r['倉庫コード']}</td>
                      <td className="text-xs">{r['倉庫名']}</td>
                      <td className="text-xs font-mono">{r['商品大分類']}</td>
                      <td className="text-xs font-mono">{r['商品中分類']}</td>
                      <td className="text-xs font-mono">{r['商品小分類']}</td>
                      <td className="text-xs font-mono">{r['商品コード']}</td>
                      <td>{r['品名']}</td>
                      <td className="text-xs text-muted">{r['規格']}</td>
                      <td className="text-xs font-mono">{r['ゾーンコード']}</td>
                      <td className="text-xs">{r['ゾーン名']}</td>
                      <td className="text-xs font-mono">{r['エリア']}</td>
                      <td className="text-xs font-mono">{r['アドレス']}</td>
                      <td className="text-xs font-mono">{r['サブアドレス']}</td>
                      <td className="text-xs text-center">{r['発注点数']}</td>
                      <td className="text-xs text-center">{r['最大在庫数']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} perPage={PER_PAGE} total={data.total} onChange={setPage} />
        </>
      )}
    </>
  );
}
